// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/feature-export/delivery.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/export/src/delivery.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo bible-strong-offline-app
//     node fleet/fleet.ts check --repo bible-strong-offline-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * EXPORT — how a finished document reaches the user.
 *
 * PLUGIN-KERNEL, feature #2. Almost all of `export/` stays a plain library:
 * `csv.ts`, `html.ts`, `types.ts` and the entity adapters register nothing with
 * any host, so by the criterion in VEREDICTO §1 they are not plugins and never
 * should be.
 *
 * The exception is THIS file's `ExportDelivery`. Handing a file to a human is
 * capability-bound: a browser tab can only offer a Blob download, a desktop
 * WebView must go through a native save dialog because it does not reliably
 * honour `<a download>`. That decision belongs to the host, once, not to every
 * call site — which is why the delivery is published as a kernel service and
 * everything above it stays pure.
 */
import { datasetToCsv } from "./csv";
import { datasetToHtml } from "./html";
import type { ExportDataset } from "./types";

export type ExportFormat = "csv" | "pdf";

export interface ExportDelivery {
  /** Human-readable name, for logs and tests. */
  readonly kind: "browser" | "file-system";
  /** Hands a text document to the user. */
  saveText(filename: string, content: string, mimeType: string): Promise<void>;
  /**
   * A print-ready HTML document. A browser opens it and calls `print()` (the
   * user picks "Save as PDF"); a desktop host writes the file and lets the
   * system handle it.
   */
  deliverPrintable(filename: string, html: string): Promise<void>;
}

/** Slugifies a title into a safe filename stem. */
export function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, "-")
      .replaceAll(/^-+|-+$/g, "")
      .slice(0, 60) || "export"
  );
}

/** `<slug>-<yyyy-mm-dd>.<ext>` from the dataset metadata. */
export function exportFilename(dataset: ExportDataset, extension: string): string {
  const stem = slugify(dataset.metadata.title ?? "export");
  const date = (dataset.metadata.generatedAt ?? new Date().toISOString()).slice(0, 10);
  return `${stem}-${date}.${extension}`;
}

/**
 * Serializes a dataset and hands it to the injected delivery. Pure
 * orchestration: no DOM, no Tauri, no branch on the platform.
 */
export async function exportDataset(
  dataset: ExportDataset,
  format: ExportFormat,
  delivery: ExportDelivery,
  filename?: string,
): Promise<void> {
  if (format === "csv") {
    await delivery.saveText(
      filename ?? exportFilename(dataset, "csv"),
      datasetToCsv(dataset),
      "text/csv",
    );
    return;
  }
  await delivery.deliverPrintable(
    filename ?? exportFilename(dataset, "html"),
    datasetToHtml(dataset),
  );
}
